/** Catalog import completion and independently measured verification are separate facts. */
export const CHECKLIST_IMPORT_RESULT_POLICY = 'IMPORT_RESULT_V1' as const;
export const CHECKLIST_IMPORT_RESULT_LIMITS = Object.freeze({
  defaultPageSize: 50,
  maxPageSize: 100,
  maxBatchProducts: 5000,
  maxPageBytes: 512 * 1024,
  maxLocalManifestBytes: 8 * 1024 * 1024,
  maxReportBytes: 256 * 1024,
  maxSamples: 20,
  maxMetadataBytes: 48 * 1024,
  retentionSeconds: 30 * 24 * 60 * 60,
  maxQuantum: 90,
  maxAttemptBytes: 2 * 1024 * 1024,
  maxStepBytes: 8 * 1024 * 1024,
  maxStepCommands: 32,
  maxCommands: 64,
  maxDatabaseBytes: 16 * 1024 * 1024,
  maxMilliseconds: 12000,
  maxResponseBytes: 4 * 1024 * 1024,
  maxInputBytes: 8 * 1024 * 1024,
  maxCaptureMappings: 10000,
  maxCaptureReferences: 10000,
  maxCaptureDefinitionsPerKind: 2000,
  maxCaptureOverlays: 2000,
  maxCaptureImportLedger: 200,
  maxExpectedBytes: 16 * 1024 * 1024,
  maxIndexBytes: 256 * 1024,
  maxChunkBytes: 1024 * 1024,
  maxChunkRecords: 512,
  maxChunks: 64,
});
export type ChecklistImportVerificationCheck = 'PASS' | 'FAIL' | 'UNKNOWN';
export type ChecklistImportVerificationStatus =
  | 'NOT_RUN'
  | 'RUNNING'
  | 'VERIFIED_COMPLETE'
  | 'MISMATCH'
  | 'INCOMPLETE'
  | 'UNKNOWN';
export type ChecklistImportResultPhase =
  | 'NOT_RUN'
  | 'INPUT'
  | 'SOURCE_MAPPINGS'
  | 'RECIPROCAL_REFERENCES'
  | 'PARALLEL_DEFINITIONS'
  | 'SUBSET_DEFINITIONS'
  | 'CORRECTIONS'
  | 'EXPECTED_VALUES'
  | 'FINALIZING'
  | 'COMPLETE';
export interface ChecklistImportResultCounts {
  roster: number | null;
  parallelDefinitions: number | null;
  subsetDefinitions: number | null;
}
export interface ChecklistImportResultDiffCounts {
  adds: number | null;
  updates: number | null;
  unchanged: number | null;
  sourceMissing: number | null;
  sourceMissingDisposition: 'RETAINED' | 'REMOVED' | 'UNKNOWN';
}
export interface ChecklistImportResultDiff {
  roster: ChecklistImportResultDiffCounts;
  parallelDefinitions: ChecklistImportResultDiffCounts;
  subsetDefinitions: ChecklistImportResultDiffCounts;
}
export interface ChecklistImportResultEvidence {
  receiptId: string | null;
  /** Actual originally imported bytes, never replaced by a later equal-FNV upload. */
  inputSha256: string | null;
  claimedInputSha256: string | null;
  claimedInputMatch: 'MATCH' | 'MISMATCH' | 'UNKNOWN';
  expectedStateSha256: string | null;
  baselineRevision: string | null;
  completedRevision: string | null;
  coverage: 'COMPLETE' | 'UNAVAILABLE' | 'PARTIAL';
  reasonCodes: string[];
}
export interface ChecklistImportResultChecks {
  input: ChecklistImportVerificationCheck;
  providerValues: ChecklistImportVerificationCheck;
  effectiveValues: ChecklistImportVerificationCheck;
  reciprocalMappings: ChecklistImportVerificationCheck;
  definitions: ChecklistImportVerificationCheck;
  retainedState: ChecklistImportVerificationCheck;
}
export interface ChecklistImportResultSample {
  code: string;
  /** Fixed allowlisted reason. Never raw values, rows, keys or SDK messages. */
  detail: string;
  sourceItemId?: string;
  definitionId?: string;
  fieldPath?: string;
}
export interface ChecklistImportVerificationSummary {
  sessionId: string | null;
  status: ChecklistImportVerificationStatus;
  complete: boolean;
  phase: ChecklistImportResultPhase;
  version: number;
  checks: ChecklistImportResultChecks;
  observedCounts: ChecklistImportResultCounts;
  mismatchTotal: number;
  samples: ChecklistImportResultSample[];
  samplesTruncated: boolean;
  revision: string | null;
  verifiedAt: string | null;
  reportId: string | null;
  digest: string | null;
}
export interface ChecklistImportResultFreshness {
  status: 'CURRENT' | 'STALE' | 'UNKNOWN';
  checkedAt: string;
}
export interface ChecklistImportProductResult {
  sourceSystem: 'azure-sql-ebay';
  sourceTargetId: string;
  jobId: string;
  fingerprint: string;
  mode: 'ordinary' | 'replace' | 'unknown';
  importStatus:
    | 'QUEUED'
    | 'PREPARING'
    | 'RUNNING'
    | 'RETRYING'
    | 'COMPLETED'
    | 'FAILED'
    | 'UNKNOWN';
  inputCounts: ChecklistImportResultCounts;
  diff: ChecklistImportResultDiff;
  evidence: ChecklistImportResultEvidence;
  verification: ChecklistImportVerificationSummary;
  freshness: ChecklistImportResultFreshness;
}
export interface ChecklistImportResultManifest {
  schemaVersion: 1;
  reportType: 'CHECKLIST_IMPORT_RESULTS';
  policyVersion: typeof CHECKLIST_IMPORT_RESULT_POLICY;
  batchId: string;
  batchFingerprint: string;
  membership: {
    coverage: 'COMPLETE' | 'PARTIAL' | 'UNKNOWN';
    total: number | null;
    listed: number;
    nextCursor?: string;
  };
  /** A local collection is complete only after all declared members/pages were inspected. */
  complete: boolean;
  observedAt: string;
  importCounters: {
    total: number | null;
    queued: number | null;
    running: number | null;
    retrying: number | null;
    completed: number | null;
    failed: number | null;
  };
  products: ChecklistImportProductResult[];
}
export interface ChecklistImportVerificationReport {
  schemaVersion: 1;
  reportType: 'CHECKLIST_IMPORT_VERIFICATION';
  policyVersion: typeof CHECKLIST_IMPORT_RESULT_POLICY;
  reportId: string;
  digest: string;
  batchId: string;
  batchFingerprint: string;
  sourceTargetId: string;
  jobId: string;
  fingerprint: string;
  evidence: ChecklistImportResultEvidence;
  /** Hash canonical UTF-8 of the whole report with only top-level digest omitted. */
  verification: Omit<ChecklistImportVerificationSummary, 'digest' | 'reportId'>;
  /** Catalog-only evidence never proves saved-card dependencies or authorizes mutation. */
  savedReferences: { coverage: 'UNKNOWN'; count: null };
  applyAllowed: false;
  createdAt: string;
  expiresAt: string;
}
export interface ChecklistImportVerificationResponse {
  schemaVersion: 1;
  batchId: string;
  sourceTargetId: string;
  verification: ChecklistImportVerificationSummary;
  freshness: ChecklistImportResultFreshness;
  expiresAt: string;
}
export interface ChecklistImportVerificationReportResponse {
  report: ChecklistImportVerificationReport;
  freshness: ChecklistImportResultFreshness;
}
export type ChecklistImportResultRequest =
  | { operation: 'BATCH_RESULTS'; batchId: string; cursor?: string; limit?: number }
  | { operation: 'VERIFY_START'; batchId: string; sourceTargetId: string; requestId: string }
  | {
      operation: 'VERIFY_ADVANCE';
      batchId: string;
      sourceTargetId: string;
      sessionId: string;
      expectedVersion: number;
      requestId: string;
    }
  | { operation: 'VERIFY_STATUS'; batchId: string; sourceTargetId: string; sessionId: string }
  | { operation: 'VERIFY_REPORT'; batchId: string; sourceTargetId: string; reportId: string };
